import React, { useState, useEffect, useRef } from 'react';
import { Lock, Calendar, Coins, AlertCircle, CheckCircle } from 'lucide-react';
import { useWeb3, useTokenLocker, useERC20 } from '../hooks/useWeb3';
import { LockFormData } from '../types/contract';
import { formatTokenAmount, parseTokenAmount, CONTRACT_ADDRESSES } from '../utils/contracts';
import { ethers } from 'ethers';

interface LockFormProps {
  onSuccess?: () => void;
}

export const LockForm: React.FC<LockFormProps> = ({ onSuccess }) => {
  const { account, isConnected, signer } = useWeb3();
  const { lockTokens, getLockFee } = useTokenLocker({ signer, isConnected });
  const [formData, setFormData] = useState<LockFormData>({
    tokenAddress: '',
    amount: '',
    unlockDate: ''
  });
  const [lockFee, setLockFee] = useState<bigint>(0n);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tokenInfo, setTokenInfo] = useState<{ name: string; symbol: string; decimals: number } | null>(null);
  const [userBalance, setUserBalance] = useState<bigint>(0n);
  const [allowance, setAllowance] = useState<bigint>(0n);

  const { getTokenInfo, getBalance, getAllowance, approve } = useERC20(formData.tokenAddress);

  // Fetch lock fee
  useEffect(() => {
    const fetchLockFee = async () => {
      try {
        const fee = await getLockFee();
        setLockFee(fee);
      } catch (error) {
        console.error('Error fetching lock fee:', error);
      }
    };

    if (isConnected) {
      fetchLockFee();
    }
  }, [isConnected, getLockFee]);

  // Fetch token info when address changes
  useEffect(() => {
    const fetchTokenInfo = async () => {
      if (!formData.tokenAddress || !isConnected) {
        setTokenInfo(null);
        return;
      }

      try {
        const info = await getTokenInfo();
        setTokenInfo(info);
      } catch (error) {
        console.error('Error fetching token info:', error);
        setTokenInfo(null);
      }
    };

    fetchTokenInfo();
  }, [formData.tokenAddress, isConnected, getTokenInfo]);

  // Fetch user balance and allowance
  useEffect(() => {
    const fetchUserData = async () => {
      if (!formData.tokenAddress || !account || !isConnected) {
        setUserBalance(0n);
        setAllowance(0n);
        return;
      }

      try {
        const [balance, allowance] = await Promise.all([
          getBalance(account),
          getAllowance(account, CONTRACT_ADDRESSES.TOKENLOCKER)
        ]);
        setUserBalance(balance);
        setAllowance(allowance);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [formData.tokenAddress, account, isConnected, getBalance, getAllowance]);

  const handleInputChange = (field: keyof LockFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(null);
  };

  const handleApprove = async () => {
    if (!formData.tokenAddress || !formData.amount) {
      setError('Please enter token address and amount first');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const amount = parseTokenAmount(formData.amount, tokenInfo?.decimals || 18);
      await approve(CONTRACT_ADDRESSES.TOKENLOCKER, amount);
      setSuccess('Token approval successful!');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Approval failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isConnected) {
      setError('Please connect your wallet first');
      return;
    }

    if (!formData.tokenAddress || !formData.amount || !formData.unlockDate) {
      setError('Please fill in all fields');
      return;
    }

    const unlockDate = new Date(formData.unlockDate);
    if (unlockDate <= new Date()) {
      setError('Unlock date must be in the future');
      return;
    }

    const amount = parseTokenAmount(formData.amount, tokenInfo?.decimals || 18);
    if (amount > userBalance) {
      setError('Insufficient token balance');
      return;
    }

    if (amount > allowance) {
      setError('Please approve tokens first');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await lockTokens(formData);
      setSuccess('Tokens locked successfully!');
      setFormData({ tokenAddress: '', amount: '', unlockDate: '' });
      onSuccess?.();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to lock tokens');
    } finally {
      setIsLoading(false);
    }
  };

  const formatFee = (fee: bigint) => {
    return `${ethers.formatEther(fee)} ETH`;
  };

  // Date/time helpers for better UX
  const unlockInputRef = useRef<HTMLInputElement>(null);
  const pad = (n: number) => String(n).padStart(2, '0');
  const formatLocalDateTime = (d: Date) => {
    const yyyy = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
  };
  const minDate = new Date(Date.now() + 60 * 1000);
  const minDateStr = formatLocalDateTime(minDate);
  const setUnlockDate = (d: Date) => {
    handleInputChange('unlockDate', formatLocalDateTime(d));
  };

  // Derived values
  const requiredAmount: bigint =
    formData.amount && tokenInfo
      ? parseTokenAmount(formData.amount, tokenInfo.decimals)
      : 0n;
  const isApprovalSufficient: boolean =
    requiredAmount > 0n && allowance >= requiredAmount;

  return (
    <div className="bg-zinc-800/50 backdrop-blur-md rounded-xl border border-zinc-600 p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-purple-400/20 rounded-lg">
          <Lock className="w-6 h-6 text-purple-400" />
        </div>
        <div>
          <h2 className="text-2xl font-zenjirou text-white">Lock Tokens</h2>
          <p className="text-zinc-400 text-sm">Secure your tokens with time-based locks</p>
        </div>
      </div>

      {!isConnected ? (
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <p className="text-zinc-400">Please connect your wallet to continue</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Token Address */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Token Address
            </label>
            <input
              type="text"
              value={formData.tokenAddress}
              onChange={(e) => handleInputChange('tokenAddress', e.target.value)}
              placeholder="0x..."
              className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white placeholder-zinc-400 focus:outline-none focus:border-purple-400 transition-colors"
            />
            {tokenInfo && (
              <div className="mt-2 flex items-center gap-2 text-sm text-zinc-400">
                <Coins className="w-4 h-4" />
                <span>{tokenInfo.name} ({tokenInfo.symbol})</span>
              </div>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Amount
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                placeholder="0.0"
                className="w-full pl-4 pr-16 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white placeholder-zinc-400 focus:outline-none focus:border-purple-400 transition-colors"
              />
              <button
                type="button"
                onClick={() => {
                  if (!tokenInfo) return;
                  const maxStr = formatTokenAmount(userBalance, tokenInfo.decimals || 18);
                  handleInputChange('amount', maxStr);
                }}
                disabled={!tokenInfo || userBalance === 0n}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 text-xs font-semibold rounded-md border border-purple-400/30 text-purple-300 hover:bg-purple-400/10 disabled:opacity-100 disabled:cursor-not-allowed"                
                aria-label="Set maximum amount"
              >
                MAX
              </button>
            </div>
            {userBalance > 0n && (
              <div className="mt-2 text-sm text-zinc-400">
                Balance: {formatTokenAmount(userBalance, tokenInfo?.decimals || 18)} {tokenInfo?.symbol}
              </div>
            )}
          </div>

          {/* Unlock Date */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Unlock Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
              <input
                type="datetime-local"
                value={formData.unlockDate}
                onChange={(e) => handleInputChange('unlockDate', e.target.value)}
                ref={unlockInputRef}
                min={minDateStr}
                step={60}
                className="hide-native-picker w-full pl-10 pr-12 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:border-purple-400 transition-colors"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md border border-purple-400/30 text-purple-300 hover:bg-purple-400/10"
                aria-label="Open date picker"
                onClick={() => {
                  const el = unlockInputRef.current as any;
                  if (!el) return;
                  if (typeof el.showPicker === 'function') {
                    el.showPicker();
                  } else {
                    el.focus();
                  }
                }}
              >
                <Calendar className="w-4 h-4" />
              </button>
            </div>
            {/* Quick date presets */}
            <div className="mt-2 flex flex-wrap gap-2 justify-center">
              {[
                /* { label: '+1h', ms: 1 * 60 * 60 * 1000 },
                { label: '+1d', ms: 24 * 60 * 60 * 1000 }, */
                { label: '+7d', ms: 7 * 24 * 60 * 60 * 1000 },
                { label: '+1m', ms: 30 * 24 * 60 * 60 * 1000 },
                { label: '+2m', ms: 90 * 24 * 60 * 60 * 1000 },
                { label: '+6m', ms: 180 * 24 * 60 * 60 * 1000 },
                { label: '+1y', ms: 365 * 24 * 60 * 60 * 1000 }
              ].map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => setUnlockDate(new Date(Date.now() + opt.ms))}
                  className="px-3 py-1 text-xs font-semibold rounded-md border border-purple-400/30 text-purple-300 hover:bg-purple-400/10"
                >  {/** border-zinc-500 text-zinc-200 hover:bg-zinc-700/60 */}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Lock Fee */}
          <div className="bg-zinc-700/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-zinc-300">Lock Fee:</span>
              <span className="text-purple-400 font-medium">{formatFee(lockFee)}</span>
            </div>
          </div>

          {/* Primary Action (Approve or Lock) */}
          {formData.tokenAddress && formData.amount && tokenInfo && (
            <div className="bg-zinc-700/30 rounded-lg p-4">
              {!isApprovalSufficient ? (
                <button
                  type="button"
                  onClick={async () => {
                    await handleApprove();
                    try {
                      if (account) {
                        const updated = await getAllowance(account, CONTRACT_ADDRESSES.TOKENLOCKER);
                        setAllowance(updated);
                      }
                    } catch {}
                  }}
                  disabled={isLoading || requiredAmount === 0n}
                  className="w-full px-4 py-2 bg-yellow-400/20 border border-yellow-400/30 rounded-lg text-yellow-400 hover:bg-yellow-400/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Approving...' : 'Approve Tokens'}
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isLoading || !isConnected || requiredAmount === 0n}
                  className="w-full px-6 py-3 bg-purple-400/20 border border-purple-400/30 rounded-lg text-purple-400 hover:bg-purple-400/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrine-button"
                >
                  {isLoading ? 'Locking Tokens...' : 'Lock Tokens'}
                </button>
              )}
            </div>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-800 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <span className="text-red-400">{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-900/20 border border-green-800 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-green-400">{success}</span>
            </div>
          )}

          {/* Fallback submit button removed in favor of single primary action above */}
        </form>
      )}
    </div>
  );
};
